import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import './ProChart.css';

const DEFAULT_HEIGHT = 320;
const MIN_HEIGHT = 180;
const MAX_HEIGHT = 700;

function buildChartUrl(symbol) {
  const sym = encodeURIComponent(symbol || 'BINANCE:BTCUSDT.P');
  const overrides = encodeURIComponent(JSON.stringify({
    'mainSeriesProperties.candleStyle.upColor': '#22c55e',
    'mainSeriesProperties.candleStyle.downColor': '#ef4444',
    'mainSeriesProperties.candleStyle.borderUpColor': '#22c55e',
    'mainSeriesProperties.candleStyle.borderDownColor': '#ef4444',
    'mainSeriesProperties.candleStyle.wickUpColor': '#22c55e',
    'mainSeriesProperties.candleStyle.wickDownColor': '#ef4444'
  }));
  const disabled = encodeURIComponent(JSON.stringify([
    'header_symbol_search', 'header_compare', 'header_undo_redo',
    'header_screenshot', 'header_fullscreen_button'
  ]));

  return 'https://www.tradingview.com/widgetembed/?frameElementId=tv_chart'
    + '&symbol=' + sym
    + '&interval=15'
    + '&hidesidetoolbar=1&hidetoptoolbar=0&symboledit=1&saveimage=0'
    + '&toolbarbg=0d0d0d&studies=%5B%5D&theme=dark&style=1&timezone=exchange'
    + '&withdateranges=1&showpopupbutton=0'
    + '&studies_overrides=%7B%7D'
    + '&overrides=' + overrides
    + '&enabled_features=%5B%5D'
    + '&disabled_features=' + disabled
    + '&locale=en';
}

function ProChart({ visible, symbol }) {
  const [loaded, setLoaded] = useState(false);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [dragging, setDragging] = useState(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const chartSymbol = useMemo(() => {
    if (!symbol) return 'BINANCE:BTCUSDT.P';
    const base = symbol.replace(/USDT$|USDC$|USD$/i, '');
    return `BINANCE:${base}USDT.P`;
  }, [symbol]);

  const chartUrl = useMemo(() => buildChartUrl(chartSymbol), [chartSymbol]);

  useEffect(() => {
    if (visible && !loaded) setLoaded(true);
  }, [visible, loaded]);

  const handleMouseMove = useCallback((e) => {
    const delta = e.clientY - startYRef.current;
    const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startHeightRef.current + delta));
    setHeight(newHeight);
  }, []);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    startYRef.current = e.clientY;
    startHeightRef.current = height;
    setDragging(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [height, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <div
      className="pro-tv-wrap"
      style={{ height: visible ? height : 0, transition: dragging ? 'none' : undefined }}
    >
      <div className="pro-tv-inner">
        {loaded && (
          <iframe
            src={chartUrl}
            className="pro-tv-iframe"
            allowTransparency="true"
            frameBorder="0"
            title="TradingView Chart"
          />
        )}
      </div>
      {dragging && <div className="pro-tv-drag-overlay" />}
      {visible && <div className="pro-tv-resize-handle" onMouseDown={handleMouseDown}><span className="pro-tv-resize-grip" /></div>}
    </div>
  );
}

export default ProChart;
